import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  GatingConfigurationResponse,
  GatingTriggerType,
} from "src/types/experiences/storyboardSettings";
import { StoryboardType } from "src/types/experiences/storyboardSettings/storyboard";

import SwitchButton from "src/components/switchButton";
import TruncateWithTooltip from "src/components/truncateWithTooltip";

import { DropDown } from "../dropdown";
import { usePageSlidesQuery } from "src/query/experiences/storyboardSettings/pages";
import { buildCleverstoryUrl } from "src/pages/experiences/utils/cleverstoryRedirect";
import { toastFailure } from "../../../utils/notifications";

import {
  GATING_TRIGGER_TYPES,
  TRIGGER_UNIT_LABEL,
  getTriggerOptions,
  isInteractiveContentType,
  isSlideStoryboardType,
  resolveGatingTriggerType,
} from "./constants";

import cx from "classnames";
import shared from "../siteSettings.module.scss";
import styles from "./gatingTriggersCard.module.scss";

interface GatingRowProps {
  config: GatingConfigurationResponse;
  storyboardType?: StoryboardType;
  catalogOptions: { id?: string; name: string; progressive?: boolean }[];
  pageOptions: { id: string; name: string; entityType?: string }[];
  sectionCount?: number;
  sectionLoading?: boolean;
  isSmartEnabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onEdit: (data: GatingConfigurationResponse, silent?: boolean) => void;
  onDelete: () => void;
  catalogLoading?: boolean;
  catalogHasMore?: boolean;
  loadMoreCatalog?: () => void;
  editable: boolean;
}

const SELECT_GATING_PLACEHOLDER = { key: "", label: "Select gating" };
const SELECT_PAGE_PLACEHOLDER = { key: "", label: "All Pages" };
const SELECT_SLIDE_PAGE_PLACEHOLDER = { key: "", label: "Select Page" };
const SELECT_SECTION_PLACEHOLDER = { key: "", label: "Select Section" };
const SLIDE_LOADING_PLACEHOLDER = { key: "", label: "Loading..." };
const SLIDE_NO_PAGE_PLACEHOLDER = { key: "", label: "Select Page first" };

const SLIDES_PAGE_SIZE = 50;

const AUTO_TRIGGER_TOAST = "Auto Trigger is already active on another gating.";

const GatingRow: React.FC<GatingRowProps> = ({
  config,
  storyboardType,
  catalogOptions,
  pageOptions,
  sectionCount = 0,
  sectionLoading = false,
  isSmartEnabled = false,
  onEdit,
  onDelete,
  catalogLoading,
  catalogHasMore,
  loadMoreCatalog,
  editable,
}) => {
  const { workspaceId } = useParams();

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const showNameInput = isHovered || isFocused;
  const displayName = nameDraft ?? config.name ?? "";

  const handleNameChange = (value: string) => {
    setNameDraft(value);
    if (!value) return;
    onEdit({ ...config, name: value });
  };

  const smart = Boolean(config.smart);
  const autoGating = Boolean(config.autoGating);

  const isInteractive = isInteractiveContentType(storyboardType);
  const isSlideStoryboard = isSlideStoryboardType(storyboardType);

  const rawTrigger = config.trigger ?? { type: "TIME" as GatingTriggerType, value: 10 };
  const triggerType = resolveGatingTriggerType({
    smart,
    autoGating,
    triggerType: rawTrigger.type,
  });
  const triggerValue = rawTrigger.value;

  const unitLabel = TRIGGER_UNIT_LABEL[triggerType];

  const selectedGatingId = config.gating?.id ?? undefined;
  const selectedGatingProgressive = Boolean(config.gating?.progressive);
  const selectedPageId = config.pageId ?? config.target?.id ?? "";

  useEffect(() => {
    if (storyboardType !== "FLIP_BOOK") return;
    if (config.trigger?.type !== "FLIPBOOK_SLIDE") return;
    onEdit(
      {
        ...config,
        trigger: { ...config.trigger, type: GATING_TRIGGER_TYPES.SPATIAL_SLIDE },
        smart: false,
      },
      true,
    );
  }, [storyboardType, config.trigger?.type]);

  const triggerTypeOptions = useMemo(() => {
    const options = getTriggerOptions(storyboardType, "page");
    return options
      .filter(
        (opt) => !(opt.value === "SMART" && selectedGatingProgressive),
      )
      .map((opt) => ({ key: opt.value, label: opt.label }));
  }, [storyboardType, selectedGatingProgressive]);

  const selectedTriggerType = useMemo(
    () =>
      triggerTypeOptions.find((opt) => opt.key === triggerType) ??
      triggerTypeOptions[0] ?? { key: triggerType, label: "" },
    [triggerTypeOptions, triggerType],
  );

  const selectGatingOptions = useMemo(
    () =>
      catalogOptions
        .filter((opt) => !(opt.progressive && smart))
        .map((opt) => ({
          key: opt.id ?? opt.name,
          label: opt.name,
        })),
    [catalogOptions, smart],
  );

  const selectedGatingOption = useMemo(
    () =>
      selectGatingOptions.find((opt) => opt.key === selectedGatingId) ??
      SELECT_GATING_PLACEHOLDER,
    [selectGatingOptions, selectedGatingId],
  );

  const isProgressiveSelected = selectedGatingProgressive && !smart;

  const showPageSection = !isInteractive;

  const selectPageOptions = useMemo(() => {
    const pages = pageOptions.map((opt) => ({ key: opt.id, label: opt.name }));
    if (isSlideStoryboard) return pages;
    return [SELECT_PAGE_PLACEHOLDER, ...pages];
  }, [pageOptions, isSlideStoryboard]);

  const selectedPageOption = useMemo(() => {
    if (smart) {
      if (isSlideStoryboard) {
        const first = selectPageOptions[0];
        if (first) return first;
      }
      return SELECT_PAGE_PLACEHOLDER;
    }
    const match = selectPageOptions.find((opt) => opt.key === selectedPageId);
    if (match) return match;
    if (!isSlideStoryboard) return SELECT_PAGE_PLACEHOLDER;

    if (selectedPageId) return SELECT_SLIDE_PAGE_PLACEHOLDER;
    return selectPageOptions[0] ?? SELECT_SLIDE_PAGE_PLACEHOLDER;
  }, [selectPageOptions, selectedPageId, isSlideStoryboard, smart]);

  useEffect(() => {
    if (smart) {
      if (
        config.target ||
        config.pageId ||
        config.trigger ||
        selectedGatingProgressive
      )
        onEdit(
          {
            ...config,
            target: null,
            pageId: undefined,
            trigger: null,
            gating: selectedGatingProgressive ? null : config.gating,
          },
          true,
        );
      return;
    }
    if (isInteractive) return;
    if (!isSlideStoryboard) return;
    if (selectedPageId) return;
    const first = pageOptions[0];
    if (!first) return;
    onEdit(
      {
        ...config,
        pageId: first.id,
        target: { id: first.id, name: first.name, type: first.entityType },
      },
      true,
    );
  }, [
    isInteractive,
    isSlideStoryboard,
    smart,
    selectedPageId,
    pageOptions,
    selectedGatingProgressive,
  ]);

  const sectionOptions = useMemo(
    () =>
      Array.from({ length: sectionCount }, (_, i) => ({
        key: String(i + 1),
        label: String(i + 1),
      })),
    [sectionCount],
  );
  const selectedSectionOption = useMemo(
    () =>
      sectionOptions.find((opt) => opt.key === String(triggerValue)) ??
      SELECT_SECTION_PLACEHOLDER,
    [sectionOptions, triggerValue],
  );

  const [slideLimit, setSlideLimit] = useState(SLIDES_PAGE_SIZE);
  const slidesQuery = usePageSlidesQuery(
    isSlideStoryboard ? selectedPageId || undefined : undefined,
    slideLimit,
  );
  const slideItems = useMemo(() => {
    const data = slidesQuery.data;
    return data?.records ?? data?.items ?? [];
  }, [slidesQuery.data]);
  const totalSlides = slidesQuery.data?.totalRecordsCount ?? slideItems.length;
  const slidesHasMore = slideItems.length < totalSlides;
  const loadMoreSlides = () => {
    if (slidesQuery.isLoading) return;
    if (!slidesHasMore) return;
    setSlideLimit((current) => current + SLIDES_PAGE_SIZE);
  };

  useEffect(() => {
    if (triggerType !== GATING_TRIGGER_TYPES.SPATIAL_SLIDE) return;
    if (!slideItems.length) return;
    const nextValue = slideItems.some((s) => s.uid === triggerValue)
      ? triggerValue
      : slideItems[0]?.uid;
    if (nextValue === triggerValue) return;
    onEdit(
      {
        ...config,
        trigger: { type: GATING_TRIGGER_TYPES.SPATIAL_SLIDE, value: nextValue },
      },
      true,
    );
  }, [slideItems, triggerType, triggerValue]);

  const slideOptions = useMemo(
    () => slideItems.map((s) => ({ key: s.uid, label: s.name })),
    [slideItems],
  );
  const selectedSlideOption = useMemo(() => {
    const match = slideOptions.find((opt) => opt.key === triggerValue);
    if (match) return match;
    if (!selectedPageId) return SLIDE_NO_PAGE_PLACEHOLDER;
    return SLIDE_LOADING_PLACEHOLDER;
  }, [slideOptions, triggerValue, selectedPageId]);

  const triggerLeadLabel =
    triggerType === GATING_TRIGGER_TYPES.TIME || triggerType === GATING_TRIGGER_TYPES.PERCENTAGE
      ? "Trigger after"
      : "Trigger at";

  const handleEnableToggle = () => {
    if (!config.enabled && isSmartEnabled && smart) {
      toastFailure(AUTO_TRIGGER_TOAST, "Failure");
      return;
    }
    onEdit({ ...config, enabled: !config.enabled });
  };

  const handleTriggerTypeChange = (key: string) => {
    const value = key as GatingTriggerType;

    if (value === GATING_TRIGGER_TYPES.SMART && isSmartEnabled && config.enabled) {
      toastFailure(AUTO_TRIGGER_TOAST, "Failure");
      return;
    }

    switch (value) {
      case GATING_TRIGGER_TYPES.SMART:
        onEdit({ ...config, trigger: null, smart: true, optional: true });
        return;
      case GATING_TRIGGER_TYPES.TIME:
        onEdit({
          ...config,
          trigger: { type: GATING_TRIGGER_TYPES.TIME, value: 10 },
          smart: false,
        });
        return;
      case GATING_TRIGGER_TYPES.PERCENTAGE:
        onEdit({
          ...config,
          trigger: { type: GATING_TRIGGER_TYPES.PERCENTAGE, value: 10 },
          smart: false,
        });
        return;
      case GATING_TRIGGER_TYPES.SPATIAL_SLIDE: {
        const keepValue = slideItems.some((s) => s.uid === triggerValue)
          ? triggerValue
          : slideItems[0]?.uid;
        onEdit({
          ...config,
          trigger: { type: GATING_TRIGGER_TYPES.SPATIAL_SLIDE, value: keepValue },
          smart: false,
        });
        return;
      }
      case GATING_TRIGGER_TYPES.INTERACTIVE_SECTION: {
        const conditionalValue =
          triggerType === GATING_TRIGGER_TYPES.INTERACTIVE_SECTION
            ? triggerValue
            : 1;
        onEdit({
          ...config,
          trigger: { type: GATING_TRIGGER_TYPES.INTERACTIVE_SECTION, value: conditionalValue },
          smart: false,
        });
        return;
      }
      default:
        onEdit({
          ...config,
          trigger: { ...rawTrigger, type: value },
          smart: false,
        });
    }
  };

  const handleTimeChange = (raw: string) => {
    let value = parseInt(raw, 10);
    if (value <= 0 || isNaN(value)) value = 0;
    else if (value > 120) value = 120;
    onEdit({ ...config, trigger: { type: "TIME", value } });
  };

  const handlePercentageChange = (raw: string) => {
    let value = parseInt(raw, 10);
    if (value <= 0 || isNaN(value)) value = 0;
    else if (value > 100) value = 100;
    onEdit({ ...config, trigger: { type: "PERCENTAGE", value } });
  };

  const handleEditGatingScreen = () => {
    if (!workspaceId || !selectedGatingId) return;
    const url = buildCleverstoryUrl(
      `/app/${workspaceId}/gating/${selectedGatingId}`,
    );
    if (!url) return;
    const win = window.open(url, "_blank");
    win?.focus();
  };

  const numericValue =
    typeof triggerValue === "number" && Number.isFinite(triggerValue)
      ? triggerValue
      : 10;

  return (
    <div className={styles.row}>
      <div className={styles.rowTop}>
        <div
          className={styles.rowName}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            setNameDraft(null);
          }}
        >
          {showNameInput ? (
            <div className={styles.nameForm}>
              <span className={styles.nameSizer} aria-hidden="true">
                {displayName || "Untitled Gating"}
              </span>
              <input
                className={styles.nameInput}
                style={{ maxWidth: 700, minWidth: 100 }}
                value={displayName}
                placeholder="Untitled Gating"
                disabled={!editable}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>
          ) : (
            <TruncateWithTooltip
              className={styles.gatingName}
              placement="top"
              text={config.name || "Untitled Gating"}
              style={{ maxWidth: 700 }}
            />
          )}
        </div>

        <div className={styles.rowActions}>
          <button
            type="button"
            className={styles.deleteBtn}
            onClick={onDelete}
            disabled={!editable}
            aria-label="Delete gating"
          >
            <i className="paperflite-delete" />
          </button>
          <div className={styles.actionDivider} />
          <div className={styles.actionGroup}>
            {!smart && (
              <SwitchButton
                id={`gating-optional-${config.id ?? config.uid}`}
                label="optional"
                checked={config.optional}
                disabled={!editable}
                className={cx(shared.switch, {
                  [shared.active]: config.optional,
                  [shared.disabled]: !editable,
                })}
                handleCheck={() =>
                  onEdit({ ...config, optional: !config.optional })
                }
              />
            )}
            <SwitchButton
              id={`gating-active-${config.id ?? config.uid}`}
              label="active"
              checked={config.enabled}
              disabled={!editable}
              className={cx(shared.switch, {
                [shared.active]: config.enabled,
                [shared.disabled]: !editable,
              })}
              handleCheck={handleEnableToggle}
            />
          </div>
        </div>
      </div>

      <div className={styles.rowControls}>
        <div className={styles.gatingScreenControl}>
          <DropDown
            showSelectionIcon={true}
            selected={selectedGatingOption}
            options={selectGatingOptions}
            placeholder="Select gating"
            loading={catalogLoading}
            hasMore={catalogHasMore}
            loadMore={loadMoreCatalog}
            disabled={!editable}
            onChange={(key) => {
              const id = key ? String(key) : undefined;
              const selected = id
                ? catalogOptions.find((opt) => opt.id === id)
                : undefined;
              onEdit({
                ...config,
                gating: id
                  ? {
                      id,
                      name: selected?.name,
                      progressive: selected?.progressive,
                    }
                  : null,
              });
            }}
          />
        </div>

        {selectGatingOptions.length > 0 && selectedGatingId && (
            <button
              type="button"
              className={styles.editGatingBtn}
              onClick={handleEditGatingScreen}
              disabled={!editable}
              aria-label="Open gating screen"
            >
              <i className="paperflite-redirect" />
            </button>
          )}


        {showPageSection && (
          <>
            <span className={styles.rowLabel}>Page</span>
            <DropDown
              showSelectionIcon={true}
              selected={selectedPageOption}
              options={selectPageOptions}
              className={cx({ [styles.disabledControl]: smart })}
              disabled={!editable || smart}
              onChange={(key) => {
                if (smart) return;
                const id = key ? String(key) : undefined;
                const selected = id
                  ? pageOptions.find((opt) => opt.id === id)
                  : undefined;
                onEdit({
                  ...config,
                  pageId: id,
                  target: id
                    ? { id, name: selected?.name, type: selected?.entityType }
                    : null,
                });
              }}
            />
          </>
        )}

        <span className={styles.rowLabel}>{triggerLeadLabel}</span>

        <DropDown
          showSelectionIcon={true}
          selected={selectedTriggerType}
          options={triggerTypeOptions}
          disabled={!editable}
          onChange={handleTriggerTypeChange}
        />

        {triggerType === GATING_TRIGGER_TYPES.TIME && (
          <>
            <input
              type="number"
              min={0}
              max={120}
              className={styles.numberInput}
              value={numericValue}
              disabled={!editable}
              onChange={(e) => handleTimeChange(e.target.value)}
            />
            {unitLabel && <span className={styles.rowLabel}>{unitLabel}</span>}
          </>
        )}

        {triggerType === GATING_TRIGGER_TYPES.PERCENTAGE && (
          <>
            <input
              type="number"
              min={0}
              max={100}
              className={styles.numberInput}
              value={numericValue > 100 ? 100 : numericValue}
              disabled={!editable}
              onChange={(e) => handlePercentageChange(e.target.value)}
            />
            {unitLabel && <span className={styles.rowLabel}>{unitLabel}</span>}
          </>
        )}

        {triggerType === GATING_TRIGGER_TYPES.INTERACTIVE_SECTION && (
          <DropDown
            showSelectionIcon={true}
            selected={selectedSectionOption}
            options={sectionOptions}
            placeholder="Select Section"
            loading={sectionLoading}
            loadingLabel="Loading..."
            emptyLabel="No sections available"
            disabled={!editable}
            onChange={(key) =>
              onEdit({
                ...config,
                trigger: { type: GATING_TRIGGER_TYPES.INTERACTIVE_SECTION, value: Number(key) },
                smart: false,
              })
            }
          />
        )}

        {triggerType === GATING_TRIGGER_TYPES.SPATIAL_SLIDE && (
          <DropDown
            showSelectionIcon={true}
            selected={selectedSlideOption}
            options={slideOptions}
            placeholder={selectedPageId ? "Loading..." : "Select Page first"}
            loadingLabel="Loading..."
            emptyLabel={selectedPageId ? "No slides available" : "Select a page first"}
            loading={slidesQuery.isLoading}
            hasMore={slidesHasMore}
            loadMore={loadMoreSlides}
            disabled={!editable}
            onChange={(key) =>
              onEdit({
                ...config,
                trigger: { type: GATING_TRIGGER_TYPES.SPATIAL_SLIDE, value: String(key) },
                smart: false,
              })
            }
          />
        )}
      </div>

      {isProgressiveSelected && (
        <TruncateWithTooltip
          className={styles.progressiveNotice}
          placement="top"
          text={`Progressive Gating is enabled on the "${selectedGatingOption.label}" screen.`}
        />
      )}
    </div>
  );
};

export default GatingRow;
